import React, { useState } from "react";
import {
  Text,
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  TabList,
  Tab,
  TabValue,
  DataGrid,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  DataGridHeader,
  DataGridHeaderCell,
} from "@fluentui/react-components";
import { ArrowLeft20Regular, SyncOff16Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ClientRoutes } from "../models/ClientRoutes";
import { ReindexStatusBadge } from "../components/systemadmin/ReindexStatusBadge";
import { ReindexActivityLog } from "../components/systemadmin/ReindexActivityLog";
import { FailedDocumentsList } from "../components/systemadmin/FailedDocumentsList";
import {
  useGetReindexJobStatus,
  useGetReindexJobHistory,
  useCancelJob,
} from "../hooks/useIndex";
import { JobStatus, JobHistoryItem } from "../models/Job";
import { useQueryClient } from "@skanska/vsa-client-auth";
import { ReindexButtonDialog } from "../components/systemadmin/ReindexButtonDialog";
import { ButtonWithLoading } from "../components/ButtonWithLoading";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
    height: "100%",
    overflow: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  statusHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  jobInfo: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  jobInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  tabContent: {
    marginTop: tokens.spacingVerticalM,
  },
  noJobState: {
    textAlign: "center",
    padding: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground3,
  },
  historyContainer: {
    maxHeight: "500px",
    overflowY: "auto",
  },
  errorState: {
    textAlign: "center",
    padding: tokens.spacingVerticalL,
  },
  loadingState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXL,
  },
});

const ReindexJobMonitoringPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState<TabValue>("current");

  const {
    data: jobStatusData,
    isLoading: jobStatusLoading,
    error: jobStatusError,
  } = useGetReindexJobStatus();

  const {
    data: historyData,
    isLoading: historyLoading,
    error: historyError,
  } = useGetReindexJobHistory();

  const { mutateAsync: cancelJob, isPending: cancelJobLoading } =
    useCancelJob();

  const handleCancelJob = async () => {
    await cancelJob();
  };

  const handleRefreshCache = async () => {
    await queryClient.invalidateQueries({ queryKey: ["reindex-job-status"] });
    await queryClient.invalidateQueries({ queryKey: ["reindex-job-history"] });

    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] === "reindex-job-history" ||
        (typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("reindex-job-history-")),
    });
  };

  const formatDuration = (startTime: string, endTime?: string): string => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end.getTime() - start.getTime();

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const historyColumns: TableColumnDefinition<JobHistoryItem>[] = [
    createTableColumn<JobHistoryItem>({
      columnId: "jobId",
      compare: (a, b) => a.id.localeCompare(b.id),
      renderHeaderCell: () => t("reindexHistory.jobId"),
      renderCell: (item) => (
        <Text weight="semibold">{item.id.substring(0, 8)}</Text>
      ),
    }),
    createTableColumn<JobHistoryItem>({
      columnId: "status",
      compare: (a, b) => a.status - b.status,
      renderHeaderCell: () => t("reindexHistory.status"),
      renderCell: (item) => <ReindexStatusBadge status={item.status} />,
    }),
    createTableColumn<JobHistoryItem>({
      columnId: "startTime",
      compare: (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      renderHeaderCell: () => t("reindexHistory.startTime"),
      renderCell: (item) => (
        <Text>{new Date(item.startTime).toLocaleString()}</Text>
      ),
    }),
    createTableColumn<JobHistoryItem>({
      columnId: "duration",
      compare: (a, b) => (a.duration || "").localeCompare(b.duration || ""),
      renderHeaderCell: () => t("reindexHistory.duration"),
      renderCell: (item) => (
        <Text>
          {item.duration ||
            (item.endTime
              ? formatDuration(item.startTime, item.endTime)
              : t("reindexHistory.inProgress"))}
        </Text>
      ),
    }),
    createTableColumn<JobHistoryItem>({
      columnId: "documents",
      compare: (a, b) =>
        (a.progress?.totalItems || 0) - (b.progress?.totalItems || 0),
      renderHeaderCell: () => t("reindexHistory.totalItems"),
      renderCell: (item) => <Text>{item.progress?.totalItems || "-"}</Text>,
    }),
    createTableColumn<JobHistoryItem>({
      columnId: "failures",
      compare: (a, b) =>
        (a.failedDocuments?.length || 0) - (b.failedDocuments?.length || 0),
      renderHeaderCell: () => t("reindexHistory.failedDocuments"),
      renderCell: (item) => (
        <Text color={item.failedDocuments?.length ? "danger" : undefined}>
          {item.failedDocuments?.length || 0}
        </Text>
      ),
    }),
    createTableColumn<JobHistoryItem>({
      columnId: "error",
      compare: (a, b) =>
        (a.errorMessage || "").localeCompare(b.errorMessage || ""),
      renderHeaderCell: () => t("reindexHistory.error"),
      renderCell: (item) => (
        <Text color={item.errorMessage ? "danger" : undefined} truncate>
          {item.errorMessage || "-"}
        </Text>
      ),
    }),
  ];

  if (jobStatusLoading && !jobStatusData) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingState}>
          <Spinner label={t("reindexMonitoring.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Button
          icon={<ArrowLeft20Regular />}
          appearance="subtle"
          className={classes.backButton}
          onClick={() => navigate(ClientRoutes.SystemAdmin)}
        >
          {t("reindexMonitoring.backToAdmin")}
        </Button>

        <Text size={600} weight="semibold">
          {t("reindexMonitoring.title")}
        </Text>

        <div style={{ display: "flex", gap: tokens.spacingHorizontalS }}>
          <Button
            icon={<SyncOff16Regular />}
            appearance="subtle"
            onClick={handleRefreshCache}
            title="Refresh cache and refetch data"
          >
            {t("reindexMonitoring.update")}
          </Button>

          {(!jobStatusData ||
            !jobStatusData?.status ||
            jobStatusData?.status !== JobStatus.Running) && (
            <ReindexButtonDialog entityName="index" />
          )}
          {jobStatusData?.status === JobStatus.Running && (
            <ButtonWithLoading
              width="200"
              text={t("cancelJob")}
              isLoading={cancelJobLoading}
              onClick={handleCancelJob}
            />
          )}
        </div>
      </div>
      {jobStatusData ? (
        <>
          {jobStatusError && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>
                  {t("reindexMonitoring.errorLoadingStatus")}
                </MessageBarTitle>
                {String(jobStatusError)}
              </MessageBarBody>
            </MessageBar>
          )}

          <Card>
            <CardHeader className={classes.statusHeader}>
              <Text size={500} weight="semibold">
                {t("reindexMonitoring.currentJobStatus")}
              </Text>
              {jobStatusData && (
                <ReindexStatusBadge status={jobStatusData.status} />
              )}
            </CardHeader>
            {!jobStatusData ? (
              <div className={classes.noJobState}>
                <Text>{t("reindexMonitoring.noActiveJob")}</Text>
              </div>
            ) : (
              <div className={classes.jobInfo}>
                <div className={classes.jobInfoRow}>
                  <Text>{t("reindexMonitoring.jobId")}:</Text>
                  <Text weight="semibold">{jobStatusData.id}</Text>
                </div>
                <div className={classes.jobInfoRow}>
                  <Text>{t("reindexMonitoring.startTime")}:</Text>
                  <Text>
                    {new Date(jobStatusData.startTime).toLocaleString()}
                  </Text>
                </div>
                {jobStatusData.endTime && (
                  <div className={classes.jobInfoRow}>
                    <Text>{t("reindexMonitoring.endTime")}:</Text>
                    <Text>
                      {new Date(jobStatusData.endTime).toLocaleString()}
                    </Text>
                  </div>
                )}
                <div className={classes.jobInfoRow}>
                  <Text>{t("reindexMonitoring.duration")}:</Text>
                  <Text>
                    {formatDuration(
                      jobStatusData.startTime,
                      jobStatusData.endTime
                    )}
                  </Text>
                </div>
                <div className={classes.jobInfoRow}>
                  <Text>{t("reindexMonitoring.inProgress")}:</Text>
                  <Text>{`${jobStatusData.progress.currentStep}`}</Text>
                </div>
                <div className={classes.jobInfoRow}>
                  <Text>{t("reindexMonitoring.progress")}:</Text>
                  <Text>{`${jobStatusData.progress.percentageComplete.toFixed()} %`}</Text>
                </div>
                {jobStatusData.errorMessage && (
                  <div className={classes.jobInfoRow}>
                    <Text>{t("reindexHistory.error")}:</Text>
                    <Text color="danger">{jobStatusData.errorMessage}</Text>
                  </div>
                )}
              </div>
            )}
          </Card>

          <TabList
            selectedValue={selectedTab}
            onTabSelect={(_, data) => setSelectedTab(data.value)}
          >
            <Tab value="current">{t("reindexMonitoring.currentJobTab")}</Tab>
            <Tab value="history">{t("reindexMonitoring.historyTab")}</Tab>
          </TabList>

          <div className={classes.tabContent}>
            {selectedTab === "current" && jobStatusData && (
              <>
                <ReindexActivityLog
                  activities={jobStatusData.searchActivities || []}
                />
                {(jobStatusData.failedDocuments?.length || 0) > 0 && (
                  <FailedDocumentsList
                    failedDocuments={jobStatusData.failedDocuments || []}
                  />
                )}
              </>
            )}

            {selectedTab === "history" && (
              <Card>
                <Text weight="semibold">{t("reindexHistory.title")}</Text>
                {historyLoading ? (
                  <div className={classes.loadingState}>
                    <Spinner label={t("reindexHistory.loading")} />
                  </div>
                ) : historyError ? (
                  <div className={classes.errorState}>
                    <Text color="danger">
                      {t("reindexHistory.errorLoading")}
                    </Text>
                  </div>
                ) : !historyData?.length ? (
                  <div className={classes.noJobState}>
                    <Text>{t("reindexHistory.noHistory")}</Text>
                  </div>
                ) : (
                  <div className={classes.historyContainer}>
                    <DataGrid
                      items={historyData}
                      columns={historyColumns}
                      sortable
                      getRowId={(item) => item.id}
                    >
                      <DataGridHeader>
                        <DataGridRow>
                          {({ renderHeaderCell }) => (
                            <DataGridHeaderCell>
                              {renderHeaderCell()}
                            </DataGridHeaderCell>
                          )}
                        </DataGridRow>
                      </DataGridHeader>
                      <DataGridBody<JobHistoryItem>>
                        {({ item }) => (
                          <DataGridRow<JobHistoryItem> key={item.id}>
                            {({ renderCell }) => (
                              <DataGridCell>{renderCell(item)}</DataGridCell>
                            )}
                          </DataGridRow>
                        )}
                      </DataGridBody>
                    </DataGrid>
                  </div>
                )}
              </Card>
            )}
          </div>
        </>
      ) : (
        <>
          <div className={classes.container}>
            <div className={classes.loadingState}>
              <Text>{t("reindexMonitoring.noActiveJob")}</Text>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReindexJobMonitoringPage;
